import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNav } from "../../components/NavContext";
import { Bar } from "react-chartjs-2";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const DoctorMetrics = () => {
  const [mapVisits, setMapVisits] = useState([]);
  const [profileVisits, setProfileVisits] = useState([]);
  const [devicesCreated, setDevicesCreated] = useState([]);
  const [signedUpDevices, setSignedUpDevices] = useState([]);
  const [locationMetrics, setLocationMetrics] = useState({});
  const [locationSummary, setLocationSummary] = useState({});
  const [imageAnalysisMetrics, setImageAnalysisMetrics] = useState([]);
  const [imageAnalysisSummary, setImageAnalysisSummary] = useState({});
  const [uniqueDevices, setUniqueDevices] = useState([]);
  const [uniqueDevicesSummary, setUniqueDevicesSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);

  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const API_URL = process.env.REACT_APP_API_URL;

  const [deviceListDate, setDeviceListDate] = useState(new Date().toISOString().split('T')[0]);
  const [deviceList, setDeviceList] = useState(null);
  const [deviceListLoading, setDeviceListLoading] = useState(false);
  const [deviceListError, setDeviceListError] = useState(null);

  const fetchDeviceList = async () => {
    setDeviceListLoading(true);
    setDeviceListError(null);
    setDeviceList(null);
    try {
      const res = await axios.get(`${API_URL}admin/metrics/device-list-by-day`, {
        params: { date: deviceListDate },
        withCredentials: true,
      });
      setDeviceList(res.data);
    } catch (e) {
      setDeviceListError('فشل جلب البيانات');
    } finally {
      setDeviceListLoading(false);
    }
  };

  const [deviceIdInput, setDeviceIdInput] = useState('');
  const [deviceDateInput, setDeviceDateInput] = useState(new Date().toISOString().split('T')[0]);
  const [deviceActivity, setDeviceActivity] = useState(null);
  const [deviceActivityLoading, setDeviceActivityLoading] = useState(false);
  const [deviceActivityError, setDeviceActivityError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchDeviceActivity = async () => {
    if (!deviceIdInput.trim()) return;
    setDeviceActivityLoading(true);
    setDeviceActivityError(null);
    setDeviceActivity(null);
    try {
      const res = await axios.get(`${API_URL}admin/metrics/device-activity`, {
        params: { device_id: deviceIdInput.trim(), date: deviceDateInput },
        withCredentials: true,
      });
      setDeviceActivity(res.data);
    } catch (e) {
      setDeviceActivityError('فشل جلب البيانات');
    } finally {
      setDeviceActivityLoading(false);
    }
  };

  const navigate = useNavigate();
  const { addLink } = useNav();

  useEffect(() => {
    addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
    addLink("Metrics", `${DOMAIN}control/admin/metrics`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLink]);

  useEffect(() => {
    document.title = "Doctor Metrics";
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      setLoading(true);
      setError(null);

      try {
        const [doctorMetricsRes, deviceMetricsRes, locationMetricsRes, uniqueDevicesRes, imageAnalysisRes] = await Promise.all([
          axios.get(`${API_URL}admin/metrics/doctors`, { params: { days } }),
          axios.get(`${API_URL}admin/metrics/devices`, { params: { days } }),
          axios.get(`${API_URL}admin/metrics/locations`, { params: { days } }),
          axios.get(`${API_URL}admin/metrics/unique-devices`, { params: { days } }),
          axios.get(`${API_URL}admin/metrics/image-analysis`, { params: { days } })
        ]);
        
        setMapVisits(doctorMetricsRes.data.map_visits || []);
        setProfileVisits(doctorMetricsRes.data.profile_visits || []);
        setDevicesCreated(deviceMetricsRes.data.devices_created || []);
        setSignedUpDevices(deviceMetricsRes.data.signed_up_devices || []);
        setLocationMetrics(locationMetricsRes.data.metrics || {});
        setLocationSummary(locationMetricsRes.data.summary || {});
        setUniqueDevices(uniqueDevicesRes.data.devices || []);
        setUniqueDevicesSummary(uniqueDevicesRes.data.summary || {});
        setImageAnalysisMetrics(imageAnalysisRes.data.image_analysis || []);
        setImageAnalysisSummary(imageAnalysisRes.data.summary || {});
      } catch (e) {
        console.error("Error fetching metrics:", e);
        setError("فشل تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [API_URL, days]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Prepare chart data for map visits
  const mapChartData = {
    labels: mapVisits.map((item) => item.day),
    datasets: [
      {
        label: "Map Visits per Day",
        data: mapVisits.map((item) => item.total_requests),
        backgroundColor: "rgba(75, 192, 192, 0.7)",
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 1,
      },
    ],
  };

  // Prepare chart data for profile visits
  const profileChartData = {
    labels: profileVisits.map((item) => item.day),
    datasets: [
      {
        label: "Profile Visits per Day",
        data: profileVisits.map((item) => item.total_requests),
        backgroundColor: "rgba(255, 99, 132, 0.7)",
        borderColor: "rgb(255, 99, 132)",
        borderWidth: 1,
      },
    ],
  };

  // Prepare chart data for devices created
  const devicesCreatedChartData = {
    labels: devicesCreated.map((item) => item.day),
    datasets: [
      {
        label: "Devices Created per Day",
        data: devicesCreated.map((item) => item.total_devices),
        backgroundColor: "rgba(54, 162, 235, 0.7)",
        borderColor: "rgb(54, 162, 235)",
        borderWidth: 1,
      },
    ],
  };

  // Prepare chart data for signed up devices
  const signedUpDevicesChartData = {
    labels: signedUpDevices.map((item) => item.day),
    datasets: [
      {
        label: "Signed Up Devices per Day",
        data: signedUpDevices.map((item) => item.total_devices),
        backgroundColor: "rgba(153, 102, 255, 0.7)",
        borderColor: "rgb(153, 102, 255)",
        borderWidth: 1,
      },
    ],
  };

  // Prepare chart data for unique devices (from X-Device-Id header)
  const uniqueDevicesChartData = {
    labels: uniqueDevices.map((item) => item.day),
    datasets: [
      {
        label: "Unique Devices per Day",
        data: uniqueDevices.map((item) => item.unique_devices),
        backgroundColor: "rgba(255, 159, 64, 0.7)",
        borderColor: "rgb(255, 159, 64)",
        borderWidth: 1,
      },
      {
        label: "Total Requests per Day",
        data: uniqueDevices.map((item) => item.total_requests),
        backgroundColor: "rgba(201, 203, 207, 0.7)",
        borderColor: "rgb(201, 203, 207)",
        borderWidth: 1,
      },
    ],
  };

  // Prepare chart data for location metrics
  const listNearbyLocationsChartData = {
    labels: locationMetrics.list_nearby_locations?.map((item) => item.day) || [],
    datasets: [
      {
        label: "Total Requests per Day",
        data: locationMetrics.list_nearby_locations?.map((item) => item.total_requests) || [],
        backgroundColor: "rgba(255, 159, 64, 0.7)",
        borderColor: "rgb(255, 159, 64)",
        borderWidth: 1,
      },
      {
        label: "Unique IPs per Day",
        data: locationMetrics.list_nearby_locations?.map((item) => item.unique_ips) || [],
        backgroundColor: "rgba(255, 99, 132, 0.7)",
        borderColor: "rgb(255, 99, 132)",
        borderWidth: 1,
      },
    ],
  };

  const suggestionsChartData = {
    labels: locationMetrics.suggestions?.map((item) => item.day) || [],
    datasets: [
      {
        label: "Total Requests per Day",
        data: locationMetrics.suggestions?.map((item) => item.total_requests) || [],
        backgroundColor: "rgba(255, 99, 255, 0.7)",
        borderColor: "rgb(255, 99, 255)",
        borderWidth: 1,
      },
      {
        label: "Unique IPs per Day",
        data: locationMetrics.suggestions?.map((item) => item.unique_ips) || [],
        backgroundColor: "rgba(54, 162, 235, 0.7)",
        borderColor: "rgb(54, 162, 235)",
        borderWidth: 1,
      },
    ],
  };

  const searchChartData = {
    labels: locationMetrics.search?.map((item) => item.day) || [],
    datasets: [
      {
        label: "Total Requests per Day",
        data: locationMetrics.search?.map((item) => item.total_requests) || [],
        backgroundColor: "rgba(75, 192, 192, 0.7)",
        borderColor: "rgb(75, 192, 192)",
        borderWidth: 1,
      },
      {
        label: "Unique IPs per Day",
        data: locationMetrics.search?.map((item) => item.unique_ips) || [],
        backgroundColor: "rgba(255, 159, 64, 0.7)",
        borderColor: "rgb(255, 159, 64)",
        borderWidth: 1,
      },
    ],
  };

  const listByCategoryNearbyChartData = {
    labels: locationMetrics.list_by_category_nearby?.map((item) => item.day) || [],
    datasets: [
      {
        label: "Total Requests per Day",
        data: locationMetrics.list_by_category_nearby?.map((item) => item.total_requests) || [],
        backgroundColor: "rgba(54, 162, 235, 0.7)",
        borderColor: "rgb(54, 162, 235)",
        borderWidth: 1,
      },
      {
        label: "Unique IPs per Day",
        data: locationMetrics.list_by_category_nearby?.map((item) => item.unique_ips) || [],
        backgroundColor: "rgba(153, 102, 255, 0.7)",
        borderColor: "rgb(153, 102, 255)",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  return (
    <div className="container mt-4" dir="rtl">
      <header className="mb-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="text-center flex-grow-1">
            <h1 className="display-6 mb-1">Doctor Metrics</h1>
            <p className="text-muted mb-0">Last {days} Days</p>
          </div>
          <button
            className="btn btn-outline-dark"
            onClick={() => navigate('/control/admin/map-activities')}
            title="View all map API activity logs"
          >
            🗺 Map Activities
          </button>
        </div>
      </header>

      {/* Days Selector */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-3">
                  <label htmlFor="daysSelect" className="form-label mb-0">
                    <strong>Number of Days:</strong>
                  </label>
                </div>
                <div className="col-md-9">
                  <select
                    id="daysSelect"
                    className="form-select"
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value))}
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={14}>Last 14 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={60}>Last 60 Days</option>
                    <option value={90}>Last 90 Days</option>
                    <option value={180}>Last 180 Days</option>
                    <option value={365}>Last 365 Days</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row g-4">
        {/* Map Visits Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">Map Visits per Day</h5>
              <small>Endpoint: DoctorController@listNearbyDoctorsByField</small>
            </div>
            <div className="card-body">
              {mapVisits.length > 0 ? (
                <Bar data={mapChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Profile Visits Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">Profile Visits per Day</h5>
              <small>Endpoint: DoctorController@fetchDoctorByAlias</small>
            </div>
            <div className="card-body">
              {profileVisits.length > 0 ? (
                <Bar data={profileChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Devices Created Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Devices Created per Day</h5>
              <small>Total user devices registered</small>
            </div>
            <div className="card-body">
              {devicesCreated.length > 0 ? (
                <Bar data={devicesCreatedChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Signed Up Devices Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-purple text-white" style={{backgroundColor: "#9966ff"}}>
              <h5 className="mb-0">Signed Up Devices per Day</h5>
              <small>Devices with user authentication (sanctum_token_id)</small>
            </div>
            <div className="card-body">
              {signedUpDevices.length > 0 ? (
                <Bar data={signedUpDevicesChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Unique Devices Chart (from X-Device-Id header) */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-dark text-white" style={{backgroundColor: "#6c757d"}}>
              <h5 className="mb-0">Unique Devices per Day</h5>
              <small>From X-Device-Id header in api_logs</small>
            </div>
            <div className="card-body">
              {uniqueDevices.length > 0 ? (
                <Bar data={uniqueDevicesChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Location Metrics Header */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
              <h4 className="mb-0">Location Metrics</h4>
              <small>LocationController endpoint usage statistics</small>
            </div>
          </div>
        </div>

        {/* List Nearby Locations Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-warning text-white" style={{backgroundColor: "#ff9f40"}}>
              <h5 className="mb-0">List Nearby Locations</h5>
              <small>Endpoint: LocationController@listNearbyLocations</small>
            </div>
            <div className="card-body">
              {locationMetrics.list_nearby_locations?.length > 0 ? (
                <Bar data={listNearbyLocationsChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Suggestions Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-info text-white" style={{backgroundColor: "#ff63ff"}}>
              <h5 className="mb-0">Location Suggestions</h5>
              <small>Endpoint: LocationController@suggestions</small>
            </div>
            <div className="card-body">
              {locationMetrics.suggestions?.length > 0 ? (
                <Bar data={suggestionsChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Search Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Location Search</h5>
              <small>Endpoint: LocationController@search</small>
            </div>
            <div className="card-body">
              {locationMetrics.search?.length > 0 ? (
                <Bar data={searchChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* List by Category & Nearby Chart */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm">
            <div className="card-header bg-secondary text-white">
              <h5 className="mb-0">List by Category & Nearby</h5>
              <small>Endpoint: LocationController@listByCategoryAndNearby</small>
            </div>
            <div className="card-body">
              {locationMetrics.list_by_category_nearby?.length > 0 ? (
                <Bar data={listByCategoryNearbyChartData} options={chartOptions} />
              ) : (
                <p className="text-muted text-center">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Summary Statistics */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-secondary text-white">
              <h5 className="mb-0">Doctor & Device Summary</h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-4">
                  <h3 className="text-info">
                    {mapVisits.reduce((sum, item) => sum + parseInt(item.total_requests || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Map Visits ({days} days)</p>
                </div>
                <div className="col-md-4">
                  <h3 className="text-danger">
                    {profileVisits.reduce((sum, item) => sum + parseInt(item.total_requests || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Profile Visits ({days} days)</p>
                </div>
                <div className="col-md-4">
                  <h3 className="text-primary">
                    {devicesCreated.reduce((sum, item) => sum + parseInt(item.total_devices || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Devices Created ({days} days)</p>
                </div>
              </div>
              <div className="row text-center mt-3">
                <div className="col-md-4">
                  <h3 style={{color: "#9966ff"}}>
                    {signedUpDevices.reduce((sum, item) => sum + parseInt(item.total_devices || 0), 0)}
                  </h3>
                  <p className="text-muted">Total Signed Up Devices ({days} days)</p>
                </div>
                <div className="col-md-4">
                  <h3 style={{color: "#6c757d"}}>
                    {uniqueDevicesSummary.total_unique_devices || 0}
                  </h3>
                  <p className="text-muted">Total Unique Devices (X-Device-Id)</p>
                  <small className="text-muted">
                    Avg: {uniqueDevicesSummary.average_unique_devices_per_day || 0}/day
                  </small>
                </div>
                <div className="col-md-4">
                  <h3 style={{color: "#6c757d"}}>
                    {uniqueDevicesSummary.max_unique_devices_per_day || 0}
                  </h3>
                  <p className="text-muted">Max Unique Devices / Day</p>
                  <small className="text-muted">
                    Total requests: {uniqueDevicesSummary.total_requests || 0}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Location Summary Statistics */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">Location Summary Statistics</h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3">
                  <h3 style={{color: "#ff9f40"}}>
                    {locationSummary.list_nearby_locations?.total_requests || 0}
                  </h3>
                  <p className="text-muted">List Nearby Locations</p>
                  <small className="text-muted">
                    {locationSummary.list_nearby_locations?.total_unique_ips || 0} unique IPs
                  </small>
                  <div>
                    <small className="text-muted">
                      Avg: {locationSummary.list_nearby_locations?.average_per_day || 0}/day | 
                      {locationSummary.list_nearby_locations?.average_unique_ips_per_day || 0} unique/day
                    </small>
                  </div>
                </div>
                <div className="col-md-3">
                  <h3 style={{color: "#ff63ff"}}>
                    {locationSummary.suggestions?.total_requests || 0}
                  </h3>
                  <p className="text-muted">Location Suggestions</p>
                  <small className="text-muted">
                    {locationSummary.suggestions?.total_unique_ips || 0} unique IPs
                  </small>
                  <div>
                    <small className="text-muted">
                      Avg: {locationSummary.suggestions?.average_per_day || 0}/day | 
                      {locationSummary.suggestions?.average_unique_ips_per_day || 0} unique/day
                    </small>
                  </div>
                </div>
                <div className="col-md-3">
                  <h3 className="text-primary">
                    {locationSummary.search?.total_requests || 0}
                  </h3>
                  <p className="text-muted">Location Search</p>
                  <small className="text-muted">
                    {locationSummary.search?.total_unique_ips || 0} unique IPs
                  </small>
                  <div>
                    <small className="text-muted">
                      Avg: {locationSummary.search?.average_per_day || 0}/day | 
                      {locationSummary.search?.average_unique_ips_per_day || 0} unique/day
                    </small>
                  </div>
                </div>
                <div className="col-md-3">
                  <h3 className="text-secondary">
                    {locationSummary.list_by_category_nearby?.total_requests || 0}
                  </h3>
                  <p className="text-muted">Category & Nearby</p>
                  <small className="text-muted">
                    {locationSummary.list_by_category_nearby?.total_unique_ips || 0} unique IPs
                  </small>
                  <div>
                    <small className="text-muted">
                      Avg: {locationSummary.list_by_category_nearby?.average_per_day || 0}/day | 
                      {locationSummary.list_by_category_nearby?.average_unique_ips_per_day || 0} unique/day
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Analysis Metrics */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-info text-white">
              <h4 className="mb-0">Image Analysis Metrics</h4>
              <small>Analyze-image API calls for location detection from photos</small>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3">
                  <h3 style={{color: "#17a2b8"}}>
                    {imageAnalysisSummary.total_calls || 0}
                  </h3>
                  <p className="text-muted">Total Image Analyses</p>
                  <div>
                    <small className="text-muted">
                      Avg: {imageAnalysisSummary.average_per_day || 0}/day
                    </small>
                  </div>
                </div>
                <div className="col-md-3">
                  <h3 style={{color: "#138496"}}>
                    {imageAnalysisSummary.max_per_day || 0}
                  </h3>
                  <p className="text-muted">Max Per Day</p>
                </div>
                <div className="col-md-3">
                  <h3 style={{color: "#0c5460"}}>
                    {imageAnalysisSummary.min_per_day || 0}
                  </h3>
                  <p className="text-muted">Min Per Day</p>
                </div>
                <div className="col-md-3">
                  <h3 style={{color: "#117a8b"}}>
                    {imageAnalysisMetrics.length > 0
                      ? imageAnalysisMetrics[imageAnalysisMetrics.length - 1]?.total_requests || 0
                      : 0}
                  </h3>
                  <p className="text-muted">Today</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Device List by Day */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header text-white" style={{backgroundColor: '#495057'}}>
              <h5 className="mb-0">Unique Devices by Day</h5>
              <small>List all device IDs that made requests on a specific date</small>
            </div>
            <div className="card-body">
              <div className="row g-3 align-items-end mb-3">
                <div className="col-md-4">
                  <label className="form-label"><strong>Date</strong></label>
                  <input
                    type="date"
                    className="form-control"
                    value={deviceListDate}
                    onChange={(e) => setDeviceListDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <button
                    className="btn w-100 text-white"
                    style={{backgroundColor: '#495057'}}
                    onClick={fetchDeviceList}
                    disabled={deviceListLoading}
                  >
                    {deviceListLoading ? 'جاري البحث...' : 'عرض الأجهزة'}
                  </button>
                </div>
              </div>

              {deviceListError && (
                <div className="alert alert-danger">{deviceListError}</div>
              )}

              {deviceList && (
                <div>
                  <div className="mb-2">
                    <strong>Date:</strong> {deviceList.date}
                    {' · '}
                    <strong>Unique devices:</strong> <span className="badge bg-secondary">{deviceList.total}</span>
                  </div>
                  {deviceList.total === 0 ? (
                    <p className="text-muted">No devices found for this date.</p>
                  ) : (
                    <div style={{maxHeight: '350px', overflowY: 'auto'}}>
                      <table className="table table-sm table-bordered table-hover" style={{fontSize: '0.82rem'}}>
                        <thead className="table-dark" style={{position: 'sticky', top: 0}}>
                          <tr>
                            <th>#</th>
                            <th>Device ID</th>
                            <th>Phone</th>
                            <th>Requests</th>
                            <th>First Seen</th>
                            <th>Last Seen</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceList.devices.map((d, i) => (
                            <tr key={d.device_id}>
                              <td>{i + 1}</td>
                              <td>
                                <code style={{fontSize: '0.78rem'}}>{d.device_id}</code>
                                {' '}
                                {d.is_returning && <span className="badge bg-success ms-1" title="Device was created at least 24h before this date">↩ returning</span>}
                              </td>
                              <td style={{whiteSpace: 'nowrap'}}>
                                {d.phone_number ? <code style={{fontSize: '0.78rem'}}>{d.phone_number}</code> : <span className="text-muted">—</span>}
                              </td>
                              <td><span className="badge bg-secondary">{d.request_count}</span></td>
                              <td style={{whiteSpace: 'nowrap'}}>{d.first_seen?.slice(11, 19)}</td>
                              <td style={{whiteSpace: 'nowrap'}}>{d.last_seen?.slice(11, 19)}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-dark"
                                  onClick={() => {
                                    setDeviceIdInput(d.device_id);
                                    setDeviceDateInput(deviceListDate);
                                  }}
                                >
                                  عرض النشاط
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Device Activity Lookup */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-dark text-white">
              <h5 className="mb-0">Device Activity Lookup</h5>
              <small>View all API calls made by a specific device on a given day</small>
            </div>
            <div className="card-body">
              <div className="row g-3 align-items-end mb-3">
                <div className="col-md-5">
                  <label className="form-label"><strong>Device ID</strong></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter device ID"
                    value={deviceIdInput}
                    onChange={(e) => setDeviceIdInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchDeviceActivity()}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label"><strong>Date</strong></label>
                  <input
                    type="date"
                    className="form-control"
                    value={deviceDateInput}
                    onChange={(e) => setDeviceDateInput(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <button
                    className="btn btn-dark w-100"
                    onClick={fetchDeviceActivity}
                    disabled={deviceActivityLoading || !deviceIdInput.trim()}
                  >
                    {deviceActivityLoading ? 'جاري البحث...' : 'عرض النشاط'}
                  </button>
                </div>
              </div>

              {deviceActivityError && (
                <div className="alert alert-danger">{deviceActivityError}</div>
              )}

              {deviceActivity && (
                <div>
                  <div className="mb-2">
                    <strong>Device:</strong> <code>{deviceActivity.device_id}</code>
                    {' · '}
                    <strong>Date:</strong> {deviceActivity.date}
                    {' · '}
                    <strong>Total requests:</strong> <span className="badge bg-secondary">{deviceActivity.total}</span>
                  </div>
                  {deviceActivity.total === 0 ? (
                    <p className="text-muted">No activity found for this device on this date.</p>
                  ) : (
                    <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                      <table className="table table-sm table-bordered table-hover" style={{fontSize: '0.82rem'}}>
                        <thead className="table-dark" style={{position: 'sticky', top: 0}}>
                          <tr>
                            <th>#</th>
                            <th>Time</th>
                            <th>Method</th>
                            <th>Endpoint</th>
                            <th>Status</th>
                            <th>ms</th>
                            <th>IP</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceActivity.logs.map((log, i) => (
                            <tr key={log.id} className={log.response_status >= 400 ? 'table-danger' : ''}>
                              <td>{i + 1}</td>
                              <td style={{whiteSpace: 'nowrap'}}>{log.started_at ? log.started_at.split('T')[1]?.slice(0,8) ?? log.started_at.slice(11,19) : ''}</td>
                              <td><span className={`badge bg-${log.method === 'GET' ? 'success' : log.method === 'POST' ? 'primary' : 'secondary'}`}>{log.method}</span></td>
                              <td style={{maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={log.endpoint_name || log.url}>
                                {log.endpoint_name || log.url}
                              </td>
                              <td><span className={`badge bg-${log.response_status < 300 ? 'success' : log.response_status < 400 ? 'warning' : 'danger'}`}>{log.response_status}</span></td>
                              <td>{log.response_time_ms}</td>
                              <td>{log.ip_address}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-secondary py-0 px-1"
                                  style={{fontSize: '0.72rem'}}
                                  onClick={() => setSelectedLog(log)}
                                  title="View request &amp; response"
                                >
                                  {'{}'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Request / Response detail modal */}
      {selectedLog && (
        <div
          className="modal d-block"
          style={{backgroundColor: 'rgba(0,0,0,0.55)'}}
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header bg-dark text-white py-2">
                <span style={{fontSize: '0.85rem'}}>
                  <span className={`badge bg-${selectedLog.method === 'GET' ? 'success' : selectedLog.method === 'POST' ? 'primary' : 'secondary'} me-2`}>{selectedLog.method}</span>
                  <code className="text-white">{selectedLog.url}</code>
                  {' '}
                  <span className={`badge bg-${selectedLog.response_status < 300 ? 'success' : selectedLog.response_status < 400 ? 'warning' : 'danger'} ms-2`}>{selectedLog.response_status}</span>
                  <span className="text-muted ms-3" style={{fontSize: '0.75rem'}}>{selectedLog.started_at}</span>
                </span>
                <button type="button" className="btn-close btn-close-white ms-auto" onClick={() => setSelectedLog(null)} />
              </div>
              <div className="modal-body p-0">
                <div className="row g-0">
                  <div className="col-md-6 border-end">
                    <div className="p-2 bg-light border-bottom"><strong style={{fontSize: '0.8rem'}}>Request Headers</strong></div>
                    <pre className="p-2 m-0" style={{fontSize: '0.72rem', maxHeight: '300px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                      {selectedLog.request_headers
                        ? JSON.stringify(
                            typeof selectedLog.request_headers === 'string'
                              ? JSON.parse(selectedLog.request_headers)
                              : selectedLog.request_headers,
                            null, 2
                          )
                        : '—'}
                    </pre>
                    <div className="p-2 bg-light border-top border-bottom"><strong style={{fontSize: '0.8rem'}}>Request Body</strong></div>
                    <pre className="p-2 m-0" style={{fontSize: '0.72rem', maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                      {selectedLog.request_body
                        ? (() => { try { return JSON.stringify(JSON.parse(selectedLog.request_body), null, 2); } catch { return selectedLog.request_body; } })()
                        : '—'}
                    </pre>
                  </div>
                  <div className="col-md-6">
                    <div className="p-2 bg-light border-bottom"><strong style={{fontSize: '0.8rem'}}>Response Body</strong></div>
                    <pre className="p-2 m-0" style={{fontSize: '0.72rem', maxHeight: '500px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>
                      {selectedLog.response_body
                        ? (() => { try { return JSON.stringify(JSON.parse(selectedLog.response_body), null, 2); } catch { return selectedLog.response_body; } })()
                        : '—'}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorMetrics;
